import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Button,
  LinearProgress,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WebIcon from '@mui/icons-material/Web';
import { Logo } from '../ui/Logo';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useAuth } from '../../lib/AuthContext';
import { sidebarTheme } from '../../theme';
import { trpc } from '../../lib/trpc';

/**
 * Sidebar width constant
 */
export const SIDEBAR_WIDTH = 200;

/**
 * Navigation item configuration
 */
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

/**
 * Navigation items for the sidebar
 */
const navItems: NavItem[] = [
  {
    label: 'Monitors',
    path: '/monitors',
    icon: <MonitorHeartIcon />,
  },
  {
    label: 'Status Pages',
    path: '/status-pages',
    icon: <WebIcon />,
  },
  {
    label: 'Notifications',
    path: '/notifications',
    icon: <NotificationsIcon />,
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: <SettingsIcon />,
  },
  {
    label: 'Billing',
    path: '/billing',
    icon: <CreditCardIcon />,
  },
];

/**
 * Props for Sidebar component
 */
interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

/**
 * Get initials from email
 */
function getInitials(email: string): string {
  if (!email) return '?';
  const parts = email.split('@')[0];
  if (!parts) return '?';
  return parts.charAt(0).toUpperCase();
}

/**
 * Navigation sidebar component
 * Displays navigation links to main sections of the app
 */
export function Sidebar({ open = true, onClose, variant = 'permanent' }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Fetch subscription status for trial indicator
  const { data: subscription } = trpc.billing.getSubscription.useQuery(undefined, {
    enabled: !!user,
  });

  const isOnTrial = subscription?.isOnTrial ?? false;
  const trialDaysRemaining = subscription?.trialDaysRemaining ?? 0;
  const trialProgress = isOnTrial ? ((14 - trialDaysRemaining) / 14) * 100 : 0;

  /**
   * Check if a nav item is currently active
   */
  const isActive = (path: string): boolean => {
    return location.pathname.startsWith(path);
  };

  /**
   * Handle navigation click
   */
  const handleNavClick = (path: string) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    logout();
    if (onClose) {
      onClose();
    }
  };

  const drawerContent = (
    <>
      {/* Logo */}
      <Box sx={{ px: 1.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Logo size={24} variant="white" />
        <Typography
          variant="subtitle1"
          noWrap
          component="div"
          sx={{ fontWeight: 700, color: sidebarTheme.textActive }}
        >
          SiteAwake
        </Typography>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 0.5 }} component="nav" aria-label="Main navigation">
        <List disablePadding>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ px: 1, py: 0.25 }}>
              <ListItemButton
                onClick={() => handleNavClick(item.path)}
                selected={isActive(item.path)}
                aria-current={isActive(item.path) ? 'page' : undefined}
                sx={{
                  borderRadius: 1,
                  py: 0.75,
                  color: sidebarTheme.text,
                  '&:hover': {
                    backgroundColor: sidebarTheme.backgroundHover,
                  },
                  '&.Mui-selected': {
                    backgroundColor: sidebarTheme.accent,
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: sidebarTheme.accent,
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#FFFFFF',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 32,
                    color: isActive(item.path) ? 'inherit' : sidebarTheme.textMuted,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive(item.path) ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Trial Indicator */}
      {isOnTrial && (
        <Box
          sx={{
            mx: 1,
            mb: 1,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <WorkspacePremiumIcon sx={{ fontSize: 16, color: '#10B981' }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: '#10B981' }}
            >
              Free Trial
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: sidebarTheme.text, mb: 1 }}
          >
            {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining
          </Typography>
          <LinearProgress
            variant="determinate"
            value={trialProgress}
            sx={{
              mb: 1.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#10B981',
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            fullWidth
            startIcon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/billing')}
            sx={{
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              fontSize: '0.75rem',
              py: 0.5,
              '&:hover': {
                backgroundColor: '#059669',
              },
            }}
          >
            Upgrade Now
          </Button>
        </Box>
      )}

      {/* Bottom section: User & Logout */}
      <Box sx={{ px: 1, py: 1, borderTop: `1px solid ${sidebarTheme.border}` }}>
        {user && (
          <Box sx={{ px: 1, py: 0.5, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: sidebarTheme.accent,
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
              aria-hidden="true"
            >
              {getInitials(user.email)}
            </Avatar>
            <Typography
              variant="caption"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: sidebarTheme.text,
              }}
            >
              {user.email}
            </Typography>
          </Box>
        )}
        <ListItemButton
          onClick={handleLogout}
          aria-label="Logout"
          sx={{
            borderRadius: 1,
            py: 0.75,
            color: sidebarTheme.textMuted,
            '&:hover': {
              backgroundColor: 'error.main',
              color: '#FFFFFF',
              '& .MuiListItemIcon-root': {
                color: '#FFFFFF',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: sidebarTheme.background,
          borderRight: 'none',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Sidebar;
